import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Location {
  name: string;
  address: string;
  neighborhood: string;
  phone: string;
  whatsapp: string;
  hours: string[];
  image: string;
  mapLink: string;
  features: string[];
}

const AboutLocations: React.FC = () => {
  const { elementRef: ref1, isVisible: isVisible1 } = useScrollAnimation({ triggerOnce: true });
  const { elementRef: ref2, isVisible: isVisible2 } = useScrollAnimation({ triggerOnce: true });

  const locations: Location[] = [
    {
      name: "BotoHarmony Parahyba Mall",
      address: "R. Bacharel José de Oliveira Curchatuz, 850",
      neighborhood: "Jardim Oceania - João Pessoa, PB",
      phone: "(83) 99308-5362",
      whatsapp: "5583993085362",
      hours: [
        "Segunda a Sexta: 9h às 19h",
        "Sábado: 9h às 14h",
        "Domingo: Fechado"
      ],
      image: "/assets/img/fachadas/02.webp",
      mapLink: "https://maps.google.com/?q=R.+Bacharel+José+de+Oliveira+Curchatuz,+850,+João+Pessoa,+PB",
      features: ["Estacionamento gratuito", "Acessibilidade", "Wi-Fi", "Climatizado"]
    },
    {
      name: "BotoHarmony Shopping Tambiá",
      address: "R. Dep. Odon Bezerra, 184",
      neighborhood: "Tambiá - João Pessoa, PB",
      phone: "(83) 99311-0469",
      whatsapp: "5583993110469",
      hours: [
        "Segunda a Sexta: 8h às 18h",
        "Sábado: 8h às 13h",
        "Domingo: Fechado"
      ],
      image: "/assets/img/fachadas/tambia.jpg",
      mapLink: "https://maps.google.com/?q=R.+Dep.+Odon+Bezerra,+184,+João+Pessoa,+PB",
      features: ["Fácil acesso", "Acessibilidade", "Wi-Fi", "Climatizado"]
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white overflow-hidden relative">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-800 mb-6">
            Nossas <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Unidades</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Duas unidades estrategicamente localizadas para melhor atendê-lo em João Pessoa
          </p>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
          {/* Location 1 - Parahyba Mall */}
          <div
            ref={ref1 as React.RefObject<HTMLDivElement>}
            className={`group transition-all duration-1000 ${
              isVisible1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-secondary/30">
              {/* Image/Logo Section */}
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={locations[0].image} 
                  alt={locations[0].name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Name */}
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-2">
                  {locations[0].name}
                </h3>

                {/* Address */}
                <div className="flex items-start gap-3 text-gray-600 mb-4">
                  <svg className="w-5 h-5 text-secondary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p>{locations[0].address}</p>
                    <p className="text-sm text-gray-500">{locations[0].neighborhood}</p>
                  </div>
                </div>

                {/* Phone & WhatsApp */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-3 text-gray-600">
                    <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>{locations[0].phone}</span>
                  </div>
                </div>

                {/* Hours */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
                    <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Horário de Funcionamento</span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600 pl-7">
                    {locations[0].hours.map((hour, i) => (
                      <p key={i}>{hour}</p>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {locations[0].features.map((feature, i) => (
                    <span key={i} className="px-3 py-1 bg-gradient-to-r from-secondary/10 to-primary/10 text-secondary text-xs font-semibold rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`https://wa.me/${locations[0].whatsapp}?text=Olá! Gostaria de agendar uma avaliação na unidade Parahyba Mall.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                  <a
                    href={locations[0].mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary hover:from-primary hover:to-secondary text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Ver Mapa
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Location 2 - Tambiá */}
          <div
            ref={ref2 as React.RefObject<HTMLDivElement>}
            className={`group transition-all duration-1000 ${
              isVisible2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-secondary/30">
              {/* Image/Logo Section */}
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={locations[1].image} 
                  alt={locations[1].name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Name */}
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-2">
                  {locations[1].name}
                </h3>

                {/* Address */}
                <div className="flex items-start gap-3 text-gray-600 mb-4">
                  <svg className="w-5 h-5 text-secondary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p>{locations[1].address}</p>
                    <p className="text-sm text-gray-500">{locations[1].neighborhood}</p>
                  </div>
                </div>

                {/* Phone & WhatsApp */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-3 text-gray-600">
                    <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>{locations[1].phone}</span>
                  </div>
                </div>

                {/* Hours */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
                    <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Horário de Funcionamento</span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600 pl-7">
                    {locations[1].hours.map((hour, i) => (
                      <p key={i}>{hour}</p>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {locations[1].features.map((feature, i) => (
                    <span key={i} className="px-3 py-1 bg-gradient-to-r from-secondary/10 to-primary/10 text-secondary text-xs font-semibold rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`https://wa.me/${locations[1].whatsapp}?text=Olá! Gostaria de agendar uma avaliação na unidade Tambiá.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                  <a
                    href={locations[1].mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-secondary to-primary hover:from-primary hover:to-secondary text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Ver Mapa
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA to Schedule */}
        <div className="text-center">
          <div className="inline-block bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 shadow-xl border border-gray-100">
            <p className="text-xl md:text-2xl font-serif text-gray-800 mb-6">
              Escolha a unidade mais próxima e <span className="text-secondary font-bold">agende sua avaliação</span>
            </p>
            <Link
              to="/agendar"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary to-primary hover:from-primary hover:to-secondary text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Agendar Avaliação
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutLocations;
